import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/i18n';
import type { ReactNode } from 'react';

export interface HotelDateValidationEntry {
  cityId: number;
  cityName: string;
  hotelId: number;
  checkInDate: string;
  checkOutDate: string;
  issue: 'check_in_before_arrival' | 'check_out_after_departure' | 'both';
}

interface InvalidHotelDatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entries: HotelDateValidationEntry[];
}

function IssueBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#fff5f5] px-3 py-1 text-sm leading-5 text-[#e5484d]">
      {children}
    </span>
  );
}

export default function InvalidHotelDatesDialog({
  isOpen,
  onClose,
  entries,
}: InvalidHotelDatesDialogProps) {
  const tValidation = useTranslations('trips.hotelDateValidation');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const resolveIssueLabels = (entry: HotelDateValidationEntry): string[] => {
    const labels: string[] = [];

    if (entry.issue === 'check_in_before_arrival' || entry.issue === 'both') {
      labels.push(tValidation('checkInBeforeArrival'));
    }

    if (entry.issue === 'check_out_after_departure' || entry.issue === 'both') {
      labels.push(tValidation('checkOutAfterDeparture'));
    }

    return labels;
  };

  // Group entries by city
  const entriesByCity = entries.reduce(
    (acc, entry) => {
      const existing = acc.find((item) => item.cityId === entry.cityId);
      if (existing) {
        existing.hotels.push(entry);
      } else {
        acc.push({
          cityId: entry.cityId,
          cityName: entry.cityName,
          hotels: [entry],
        });
      }
      return acc;
    },
    [] as Array<{ cityId: number; cityName: string; hotels: HotelDateValidationEntry[] }>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[30rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{tValidation('title')}</DialogTitle>

        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">{tValidation('title')}</h2>
          <Button
            onClick={onClose}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4]"
            aria-label={tValidation('close')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        <div className="px-6 pb-4">
          <p className="mb-4 text-base leading-6 text-[#292929]">{tValidation('description')}</p>

          <div className="flex flex-col gap-4">
            {entriesByCity.map((cityGroup) => (
              <div
                key={cityGroup.cityId}
                className="rounded-2xl border border-[#e5484d] bg-[#fff5f5] px-4 py-3"
              >
                <div className="mb-2 text-[15px] leading-6 font-medium text-[#292929]">
                  {cityGroup.cityName}
                </div>
                <div className="flex flex-col gap-2">
                  {cityGroup.hotels.map((hotel) => (
                    <div key={hotel.hotelId} className="flex flex-wrap gap-2">
                      {resolveIssueLabels(hotel).map((label, index) => (
                        <IssueBadge key={`${hotel.hotelId}-${index}`}>{label}</IssueBadge>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            onClick={onClose}
            className="h-auto rounded-full bg-[#242424] px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-[#1a1a1a]"
          >
            {tValidation('close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
